import { ModalWindow } from '@cian/ui-kit';
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { modifyOffer } from './modifyOffer';
import { setOffersSelectionInitial } from '../../actions/offersForSelection';
import { getOffersForSelection } from '../../actions/offersForSelection/getOffersForSelection';
import { trackSelectedOffersCancel } from '../../actions/offersForSelection/tracking';
import { sendOfferMessage } from '../../actions/sendOfferMessage';
import { OffersPickerModalFooter } from '../../components/OffersPickerModalFooter';
import { OFFERS_FOR_REQUEST_LIMIT } from '../../constants/offersSelectionLimits';
import { IOfferForClientSchema } from '../../repositories/chats/entities/for_client/OfferForClientSchema';
import { EQueryParameters as ETabType } from '../../repositories/chats/v1/get-my-offers-for-selection-desktop';
import { currentChatOpponentIdSelector, currentOpenChatIDSelector } from '../../selectors';
import { offersForSelection } from '../../selectors/offersForSelection';
import { OffersPickerContainer } from '../OffersPickerContainer';

interface IProps {
  isOpen: boolean;
  onCloseAllModals(): void;
}

const SELECT_LIMIT = 10;

export const OffersPickerModalContainer = ({ isOpen, onCloseAllModals }: IProps) => {
  const dispatch = useDispatch();

  const chatId = useSelector(currentOpenChatIDSelector) || '';
  const opponentId = useSelector(currentChatOpponentIdSelector);

  const [selectedOffers, setSelectedOffers] = React.useState<Array<IOfferForClientSchema>>([]);
  const selectedIds = React.useMemo(() => selectedOffers.map(item => item.realtyOfferId), [selectedOffers]);
  const [currentType, setCurrentType] = React.useState(ETabType.Rent);
  const isReachedLimit = React.useMemo(() => selectedOffers.length >= SELECT_LIMIT, [selectedOffers]);

  const { list: offersList, status: offersSelectionStatus, page } = useSelector(offersForSelection, shallowEqual);

  const handleOffersLoad = React.useCallback(() => {
    dispatch(
      getOffersForSelection({
        tabType: currentType,
      }),
    );
  }, [currentType, dispatch]);

  React.useEffect(() => {
    if (isOpen) {
      handleOffersLoad();
    }
  }, [handleOffersLoad, currentType, isOpen]);

  const handleOfferSelect = React.useCallback(
    (offer: IOfferForClientSchema) => {
      if (selectedIds.includes(offer.realtyOfferId)) {
        setSelectedOffers(list => list.filter(item => item.realtyOfferId !== offer.realtyOfferId));
      } else if (!isReachedLimit) {
        setSelectedOffers([...selectedOffers, offer]);
      }
    },
    [setSelectedOffers, selectedIds, selectedOffers, isReachedLimit],
  );

  const onCancel = React.useCallback(() => {
    const baseDecimalOfferPosition = page * OFFERS_FOR_REQUEST_LIMIT;
    const preparedForTrackingData = selectedOffers.map(({ realtyOfferId }, index) => ({
      id: realtyOfferId,
      offerType: 'offer',
      position: baseDecimalOfferPosition + index,
    }));

    trackSelectedOffersCancel({
      chatId,
      pageNumber: page,
      products: preparedForTrackingData,
      dealType: currentType,
      opponentId,
    });

    setSelectedOffers([]);
  }, [page, selectedOffers, chatId, currentType, opponentId]);

  const handleTypeChange = React.useCallback(
    (_: never, value: ETabType) => {
      setCurrentType(value);
      dispatch(setOffersSelectionInitial());
    },
    [setCurrentType, dispatch],
  );

  const onCloseWithReset = React.useCallback(() => {
    onCloseAllModals();
    dispatch(setOffersSelectionInitial());
    setCurrentType(ETabType.Rent);
    setSelectedOffers([]);
  }, [dispatch, setSelectedOffers, onCloseAllModals]);

  const onApply = React.useCallback(() => {
    onCloseWithReset();

    selectedOffers.forEach(offer => {
      dispatch(
        sendOfferMessage({
          offer: modifyOffer(offer),
          chatId,
        }),
      );
    });
  }, [selectedOffers, onCloseWithReset, dispatch, chatId]);

  const title = selectedIds.length ? `Выбрано ${selectedIds.length} из ${SELECT_LIMIT}` : 'Мои объявления';

  return (
    <ModalWindow
      open={isOpen}
      onClose={onCloseWithReset}
      width={560}
      fixed
      size="M"
      title={title}
      footer={
        offersList.length ? (
          <OffersPickerModalFooter selectedIds={selectedIds} onCancel={onCancel} onApply={onApply} />
        ) : (
          <div />
        )
      }
    >
      <OffersPickerContainer
        currentType={currentType}
        handleTypeChange={handleTypeChange}
        offersList={offersList}
        onSelect={handleOfferSelect}
        selectedIds={selectedIds}
        handleOffersLoad={handleOffersLoad}
        isReachedLimit={isReachedLimit}
        offersSelectionStatus={offersSelectionStatus}
      />
    </ModalWindow>
  );
};
